import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RefreshCw, ArrowDownToLine, AlertTriangle, Info, CheckCircle2, XCircle, KeyRound, Save, Trash2, History, Undo2 } from "lucide-react";

/**
 * Peer-Sync-Panel: zeigt Vergleich zwischen dieser Instanz und einer konfigurierten
 * Peer-Umgebung (Preview ↔ Production) und erlaubt Ein-Klick-Synchronisierung.
 */
export default function PeerSyncPanel() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [selected, setSelected] = useState({});
    const [removeOrphans, setRemoveOrphans] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, confirmText: "" });
    const [result, setResult] = useState(null);
    // Peer-Zugang (in-app config)
    const [cfg, setCfg] = useState(null);
    const [cfgEdit, setCfgEdit] = useState({ url: "", email: "", password: "" });
    const [cfgSaving, setCfgSaving] = useState(false);
    // Sync-Verlauf
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [rollbackDialog, setRollbackDialog] = useState({ open: false, backupId: null, tenant: null, confirmText: "" });
    const [rollingBack, setRollingBack] = useState(false);

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const r = await api.get("/admin/peer-sync-history?limit=20");
            setHistory(r.data?.eintraege || []);
        } catch (e) {
            // still, don't blow up
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const runRollback = async () => {
        if (!rollbackDialog.backupId) return;
        if (rollbackDialog.confirmText.trim().toUpperCase() !== "ROLLBACK") {
            toast.error("Bitte 'ROLLBACK' zur Bestätigung eintippen.");
            return;
        }
        setRollingBack(true);
        try {
            const r = await api.post("/admin/sync-rollback", {
                backup_id: rollbackDialog.backupId,
                bestaetigung: "ROLLBACK",
            });
            toast.success(
                `Rollback für ${r.data.tenant_id}: ${r.data.eingespielt.titles} Titel wiederhergestellt`
            );
            setRollbackDialog({ open: false, backupId: null, tenant: null, confirmText: "" });
            await load();
            await loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rollback fehlgeschlagen");
        } finally {
            setRollingBack(false);
        }
    };

    const loadConfig = async () => {
        try {
            const r = await api.get("/admin/peer-config");
            setCfg(r.data);
            setCfgEdit({
                url: r.data?.url || "https://lagerbewertung.preview.emergentagent.com",
                email: r.data?.email || "",
                password: "",
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Peer-Zugang konnte nicht geladen werden");
        }
    };

    const saveConfig = async () => {
        if (!cfgEdit.url || !cfgEdit.email || !cfgEdit.password) {
            toast.error("Bitte URL, E-Mail und Passwort ausfüllen.");
            return;
        }
        setCfgSaving(true);
        try {
            await api.post("/admin/peer-config", cfgEdit);
            toast.success("Peer-Zugang gespeichert und getestet");
            setCfgEdit((c) => ({ ...c, password: "" }));
            await loadConfig();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setCfgSaving(false);
        }
    };

    const deleteConfig = async () => {
        if (!window.confirm("Peer-Zugang wirklich entfernen? Der Sync ist danach nicht mehr möglich, bis Sie ihn neu eintragen.")) return;
        try {
            await api.delete("/admin/peer-config");
            toast.success("Peer-Zugang entfernt");
            await loadConfig();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/peer-sync-status");
            setStatus(r.data);
            if (r.data?.configured) {
                const next = {};
                for (const v of r.data.vergleich || []) {
                    if (v.state === "abweichend" || v.state === "nur_peer") {
                        next[v.tenant_id] = true;
                    }
                }
                setSelected(next);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Peer-Status konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadConfig();
        load();
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = (tid) => setSelected((s) => ({ ...s, [tid]: !s[tid] }));

    const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

    const runSync = async () => {
        if (confirmDialog.confirmText.trim().toUpperCase() !== "SYNC") {
            toast.error("Bitte 'SYNC' zur Bestätigung eintippen.");
            return;
        }
        setRunning(true);
        setResult(null);
        try {
            const r = await api.post("/admin/peer-sync-pull", {
                tenant_ids: selectedIds,
                bestaetigung: "SYNC",
                remove_local_only: removeOrphans,
            });
            setResult(r.data);
            toast.success("Peer-Sync abgeschlossen");
            setConfirmDialog({ open: false, confirmText: "" });
            await load();
            await loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Peer-Sync fehlgeschlagen");
        } finally {
            setRunning(false);
        }
    };

    // Peer-Config-Karte (immer sichtbar oben)
    const ConfigCard = (
        <Card className="p-6 bg-surface1 border-border max-w-3xl" data-testid="peer-config-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <KeyRound className="h-3.5 w-3.5" /> Peer-Zugang konfigurieren
            </div>
            <h3 className="font-heading text-xl font-semibold mt-1">Zugang zum Preview-/Peer-Server</h3>
            <p className="text-sm text-muted-foreground mt-1">
                Hinterlegen Sie hier die URL und Ihre eigenen Admin-Zugangsdaten des Peers.
                Das Passwort wird verschlüsselt in der Datenbank gespeichert und niemals im
                Klartext angezeigt.
            </p>

            {cfg?.passwort_gespeichert && (
                <div className="mt-4 flex items-center gap-2 text-sm text-emerald-400" data-testid="peer-config-active">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>
                        Aktueller Zugang: <span className="font-mono text-xs">{cfg.email}</span>
                        {" @ "}
                        <span className="font-mono text-xs">{cfg.url}</span>
                        {" · Quelle: "}
                        <span className="font-mono">{cfg.quelle}</span>
                    </span>
                </div>
            )}

            <div className="mt-5 grid gap-3 max-w-xl">
                <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Peer-URL</label>
                    <Input
                        value={cfgEdit.url}
                        onChange={(e) => setCfgEdit((c) => ({ ...c, url: e.target.value }))}
                        placeholder="https://lagerbewertung.preview.emergentagent.com"
                        className="mt-1 font-mono text-sm"
                        data-testid="peer-config-url"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Ihre Admin-E-Mail (Peer)</label>
                    <Input
                        type="email"
                        value={cfgEdit.email}
                        onChange={(e) => setCfgEdit((c) => ({ ...c, email: e.target.value }))}
                        placeholder="ihre-email@ihre-firma.at"
                        className="mt-1 font-mono text-sm"
                        data-testid="peer-config-email"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Ihr Passwort (Peer)
                        {cfg?.passwort_gespeichert && (
                            <span className="ml-2 text-muted-foreground normal-case">— nur ausfüllen, wenn geändert werden soll</span>
                        )}
                    </label>
                    <Input
                        type="password"
                        value={cfgEdit.password}
                        onChange={(e) => setCfgEdit((c) => ({ ...c, password: e.target.value }))}
                        placeholder="•••••••••"
                        className="mt-1 font-mono text-sm"
                        data-testid="peer-config-password"
                    />
                </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
                <Button
                    onClick={saveConfig}
                    disabled={cfgSaving}
                    className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                    data-testid="peer-config-save"
                >
                    <Save className="h-4 w-4 mr-2" />
                    {cfgSaving ? "Prüfe & speichere …" : "Verbindung testen & speichern"}
                </Button>
                {cfg?.quelle === "db" && (
                    <Button
                        variant="outline"
                        onClick={deleteConfig}
                        data-testid="peer-config-delete"
                    >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Zugang entfernen
                    </Button>
                )}
            </div>
            <div className="mt-3 text-xs text-muted-foreground flex items-start gap-2">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                    Beim Speichern wird der Zugang sofort per Login getestet. Nur wenn der
                    Login erfolgreich ist, wird der Datensatz gespeichert.
                </span>
            </div>
        </Card>
    );

    if (loading && !status) {
        return (
            <div className="space-y-6">
                {ConfigCard}
                <Card className="p-6 bg-surface1 border-border">
                    <div className="text-sm text-muted-foreground">Lade Peer-Status …</div>
                </Card>
            </div>
        );
    }

    if (!status?.configured) {
        return (
            <div className="space-y-6">
                {ConfigCard}
                <Card className="p-6 bg-surface1 border-border max-w-3xl" data-testid="peer-sync-not-configured">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="font-heading text-lg font-semibold">Peer-Sync noch nicht einsatzbereit</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                {status?.hint || "Bitte oben die Peer-Zugangsdaten eintragen und speichern."}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    const rows = status.vergleich || [];
    const stateStyles = {
        identisch: "text-emerald-400 border-emerald-400/40",
        abweichend: "text-amber-400 border-amber-400/40",
        nur_peer: "text-sky-400 border-sky-400/40",
        nur_lokal: "text-rose-400 border-rose-400/40",
    };
    const stateLabel = {
        identisch: "Identisch",
        abweichend: "Abweichend",
        nur_peer: "Nur auf Peer",
        nur_lokal: "Nur lokal (verwaist)",
    };

    return (
        <div className="space-y-6">
            {ConfigCard}
            <Card className="p-6 bg-surface1 border-border max-w-4xl" data-testid="peer-sync-panel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <RefreshCw className="h-3.5 w-3.5" /> Datenübernahme aus Peer-Umgebung
                </div>
                <h3 className="font-heading text-xl font-semibold mt-1">
                    Preview ↔ Production Sync
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                    Peer:{" "}
                    <span className="font-mono text-xs bg-background border border-border rounded px-1.5 py-0.5">
                        {status.peer_url}
                    </span>
                </p>
                {status.peer_error && (
                    <div className="mt-3 text-sm text-rose-400 flex items-start gap-2">
                        <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>{status.peer_error}</div>
                    </div>
                )}

                <div className="mt-5 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                            <tr className="border-b border-border">
                                <th className="text-left py-2 w-10"></th>
                                <th className="text-left py-2">Mandant</th>
                                <th className="text-right py-2">Lokal (Titel/Bew.)</th>
                                <th className="text-right py-2">Peer (Titel/Bew.)</th>
                                <th className="text-center py-2">Zustand</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((v) => {
                                const disabled = v.state === "identisch" || v.state === "nur_lokal";
                                return (
                                    <tr key={v.tenant_id} className="border-b border-border/60">
                                        <td className="py-3">
                                            <Checkbox
                                                checked={!!selected[v.tenant_id]}
                                                disabled={disabled}
                                                onCheckedChange={() => toggle(v.tenant_id)}
                                                data-testid={`peer-select-${v.tenant_id}`}
                                            />
                                        </td>
                                        <td className="py-3">
                                            <div className="font-medium">{v.name || v.tenant_id}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{v.tenant_id}</div>
                                        </td>
                                        <td className="py-3 text-right font-mono text-xs">
                                            {v.lokal ? `${v.lokal.titel} / ${v.lokal.bewegungen}` : "—"}
                                        </td>
                                        <td className="py-3 text-right font-mono text-xs">
                                            {v.peer ? `${v.peer.titel} / ${v.peer.bewegungen}` : "—"}
                                        </td>
                                        <td className="py-3 text-center">
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] ${stateStyles[v.state] || ""}`}
                                                data-testid={`peer-state-${v.tenant_id}`}
                                            >
                                                {stateLabel[v.state] || v.state}
                                            </Badge>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="mt-5 flex items-center gap-3 text-sm">
                    <Checkbox
                        id="peer-remove-orphans"
                        checked={removeOrphans}
                        onCheckedChange={setRemoveOrphans}
                        data-testid="peer-remove-orphans"
                    />
                    <label htmlFor="peer-remove-orphans" className="cursor-pointer">
                        Verwaiste lokale Mandanten entfernen (nicht auf Peer vorhanden)
                    </label>
                </div>

                <div className="mt-6 flex flex-wrap gap-3 items-center">
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading || running}
                        data-testid="peer-refresh-btn"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Status neu laden
                    </Button>
                    <Button
                        onClick={() => setConfirmDialog({ open: true, confirmText: "" })}
                        disabled={running || (selectedIds.length === 0 && !removeOrphans)}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="peer-sync-btn"
                    >
                        <ArrowDownToLine className="h-4 w-4 mr-2" />
                        {selectedIds.length} Mandant{selectedIds.length !== 1 ? "en" : ""} vom Peer holen
                    </Button>
                </div>

                <div className="mt-5 text-xs text-muted-foreground flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                        Der Sync <b>ersetzt</b> die ausgewählten lokalen Mandanten vollständig durch die
                        Daten des Peers. Bestehende Daten dieser Mandanten werden gelöscht. Andere
                        Mandanten und die Benutzerverwaltung bleiben unberührt.
                    </span>
                </div>
            </Card>

            {result && (
                <Card className="p-6 bg-surface1 border-border max-w-4xl" data-testid="peer-sync-result">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-400">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Sync-Ergebnis
                    </div>
                    <h3 className="font-heading text-lg font-semibold mt-1">Übernommene Daten</h3>
                    <ul className="mt-3 space-y-2 text-sm">
                        {result.ergebnisse?.map((e) => (
                            <li key={e.tenant_id} className="flex items-start gap-2">
                                {e.status === "ok" ? (
                                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-rose-400 mt-0.5" />
                                )}
                                <div className="min-w-0">
                                    <div className="font-medium">{e.tenant_id}</div>
                                    {e.status === "ok" ? (
                                        <div className="text-xs text-muted-foreground">
                                            Titel: {e.titel_uebernommen} · Bewegungen: {e.bewegungen_uebernommen} · Snapshots: {e.snapshots_uebernommen} · Sonder-WB: {e.sonder_wb_uebernommen}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-rose-400">{e.note}</div>
                                    )}
                                </div>
                            </li>
                        ))}
                        {result.entfernte_lokale_mandanten?.length > 0 && (
                            <li className="pt-2 border-t border-border text-xs text-muted-foreground">
                                Entfernte verwaiste Mandanten: {result.entfernte_lokale_mandanten.join(", ")}
                            </li>
                        )}
                    </ul>
                </Card>
            )}

            <Card className="p-6 bg-surface1 border-border max-w-4xl" data-testid="peer-sync-history-card">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <History className="h-3.5 w-3.5" /> Sync-Verlauf
                </div>
                <div className="flex items-center justify-between gap-4 mt-1">
                    <h3 className="font-heading text-xl font-semibold">Letzte Synchronisierungen</h3>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadHistory}
                        disabled={historyLoading}
                        data-testid="peer-sync-history-refresh"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${historyLoading ? "animate-spin" : ""}`} />
                        Aktualisieren
                    </Button>
                </div>
                {history.length === 0 ? (
                    <div className="mt-4 text-sm text-muted-foreground" data-testid="peer-sync-history-empty">
                        Noch kein Sync-Vorgang durchgeführt. Nach dem ersten Sync erscheint hier
                        die Historie mit Datum, Nutzer und Ergebnis pro Mandant.
                    </div>
                ) : (
                    <ul className="mt-4 space-y-3" data-testid="peer-sync-history-list">
                        {history.map((h) => {
                            const dt = new Date(h.zeit);
                            const dateStr = dt.toLocaleString("de-AT", {
                                dateStyle: "medium",
                                timeStyle: "short",
                            });
                            const allOk = h.fehler === 0;
                            return (
                                <li
                                    key={h.id}
                                    className="border border-border rounded p-3 bg-background"
                                    data-testid={`peer-sync-history-item-${h.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                {allOk ? (
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-rose-400" />
                                                )}
                                                <span className="font-medium text-sm">{dateStr}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    · {h.user_email}
                                                </span>
                                            </div>
                                            <div className="text-xs text-muted-foreground font-mono mt-1">
                                                Peer: {h.peer_url}
                                            </div>
                                        </div>
                                        <Badge
                                            variant="outline"
                                            className={`text-[10px] ${allOk ? "text-emerald-400 border-emerald-400/40" : "text-rose-400 border-rose-400/40"}`}
                                        >
                                            {h.erfolg} ok · {h.fehler} Fehler
                                        </Badge>
                                    </div>
                                    <ul className="mt-2 space-y-1 text-xs">
                                        {(h.ergebnisse || []).map((e, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <span className={e.status === "ok" ? "text-emerald-400" : "text-rose-400"}>●</span>
                                                <span className="font-mono">{e.tenant_id}</span>
                                                <span className="text-muted-foreground flex-1">
                                                    {e.status === "ok"
                                                        ? `Titel: ${e.titel_uebernommen}, Bew.: ${e.bewegungen_uebernommen}, Snap.: ${e.snapshots_uebernommen}`
                                                        : e.note}
                                                </span>
                                                {e.status === "ok" && e.pre_backup_id && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-6 text-[10px] px-2"
                                                        onClick={() =>
                                                            setRollbackDialog({
                                                                open: true,
                                                                backupId: e.pre_backup_id,
                                                                tenant: e.tenant_id,
                                                                confirmText: "",
                                                            })
                                                        }
                                                        data-testid={`peer-sync-rollback-${e.tenant_id}`}
                                                    >
                                                        <Undo2 className="h-3 w-3 mr-1" />
                                                        Rollback
                                                    </Button>
                                                )}
                                            </li>
                                        ))}
                                        {h.entfernte_lokale_mandanten?.length > 0 && (
                                            <li className="text-muted-foreground pl-4">
                                                Entfernt: {h.entfernte_lokale_mandanten.join(", ")}
                                            </li>
                                        )}
                                    </ul>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            <Dialog
                open={confirmDialog.open}
                onOpenChange={(v) => setConfirmDialog((d) => ({ ...d, open: v }))}
            >
                <DialogContent data-testid="peer-sync-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Sync bestätigen</DialogTitle>
                        <DialogDescription>
                            {selectedIds.length} Mandant{selectedIds.length !== 1 ? "en" : ""} werden vom Peer
                            {" "}<span className="font-mono text-xs">{status.peer_url}</span>{" "}
                            geholt und lokal vollständig ersetzt.
                            {removeOrphans && " Zusätzlich werden verwaiste lokale Mandanten entfernt."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm">Tippen Sie <b>SYNC</b> zur Bestätigung:</div>
                        <Input
                            value={confirmDialog.confirmText}
                            onChange={(e) => setConfirmDialog((d) => ({ ...d, confirmText: e.target.value }))}
                            placeholder="SYNC"
                            className="max-w-xs"
                            data-testid="peer-sync-confirm-input"
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setConfirmDialog({ open: false, confirmText: "" })}
                                disabled={running}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                onClick={runSync}
                                disabled={running || confirmDialog.confirmText.trim().toUpperCase() !== "SYNC"}
                                className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                                data-testid="peer-sync-confirm-btn"
                            >
                                {running ? "Läuft …" : "Jetzt synchronisieren"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={rollbackDialog.open}
                onOpenChange={(v) => setRollbackDialog((d) => ({ ...d, open: v }))}
            >
                <DialogContent data-testid="peer-sync-rollback-dialog">
                    <DialogHeader>
                        <DialogTitle>Rollback bestätigen</DialogTitle>
                        <DialogDescription>
                            Der Mandant <b>{rollbackDialog.tenant}</b> wird auf den Stand
                            <b> vor dem gewählten Sync</b> zurückgesetzt. Aktuelle Daten
                            werden ersetzt — ein weiteres Backup wird vorher automatisch
                            angelegt, damit dieser Rollback bei Bedarf wieder rückgängig
                            gemacht werden kann.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm">Tippen Sie <b>ROLLBACK</b> zur Bestätigung:</div>
                        <Input
                            value={rollbackDialog.confirmText}
                            onChange={(e) =>
                                setRollbackDialog((d) => ({ ...d, confirmText: e.target.value }))
                            }
                            placeholder="ROLLBACK"
                            className="max-w-xs"
                            data-testid="peer-sync-rollback-input"
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() =>
                                    setRollbackDialog({ open: false, backupId: null, tenant: null, confirmText: "" })
                                }
                                disabled={rollingBack}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                onClick={runRollback}
                                disabled={rollingBack || rollbackDialog.confirmText.trim().toUpperCase() !== "ROLLBACK"}
                                className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                                data-testid="peer-sync-rollback-confirm-btn"
                            >
                                {rollingBack ? "Läuft …" : "Jetzt zurücksetzen"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
